import { Component, inject, Inject } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  FormArray,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  allowOnlyLetters,
  allowLimitedInput,
  trimInput,
  handlePasteText,
} from '../../dis-repository/master.utils';
import { DisbursmentService } from 'src/app/shared/service/disbursment/disbursment.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-create-service',
  templateUrl: './create-service.component.html',
  styleUrls: ['./create-service.component.scss'],
})
export class CreateServiceComponent {
  loader = inject(DisbursmentService);
  isLoading = this.loader.dialogLoading$;

  disallowNumericData = allowOnlyLetters;
  allowLimitedInput = allowLimitedInput;
  serviceFormGroup: FormGroup;
  existingServices = [];
  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CreateServiceComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private snackBar: ToastrService,
  ) {
    this.existingServices = this.data.services;

    this.serviceFormGroup = this.fb.group({
      services: this.fb.array([]),
    });
    this.addService();
  }

  get serviceForm(): FormArray {
    return this.serviceFormGroup.get('services') as FormArray;
  }

  // getter for casting to FormGroup
  get serviceFormGroups(): FormGroup[] {
    return this.serviceForm.controls as FormGroup[];
  }

  // method to get the field name
  getServiceControl(index: number, controlName: string) {
    return this.serviceForm.at(index)?.get(controlName);
  }

  // method to trim the input value
  onInputTrim(index: number, controlName: string): void {
    const control = this.getServiceControl(index, controlName);
    if (control) trimInput(control);
  }

  // method to handle the paste event
  onPaste(event: ClipboardEvent, index: number, controlName: string): void {
    const value = handlePasteText(event);
    this.getServiceControl(index, controlName)?.setValue(value);
  }

  // method to add the multiple services
  addService() {
    const group = this.fb.group({
      name: ['', Validators.required],
      agentValueFromCurrency: ['', Validators.required],
      agentValueToCurrency: ['', Validators.required],
      remark: [''],
      subServices: [[]],
    });
    this.serviceForm.push(group);
  }

  // method to remove the servi ce at the particular index
  removeService(index: number) {
    this.serviceForm.removeAt(index);
  }

  // Check if the service is a duplicate (either existing or within the current form)
  isDuplicateService(
    serviceName: string,
    currentServiceIndex: number,
  ): boolean {
    const isDuplicateInExisting = this.existingServices.some(
      (service: FormGroup) => {
        const existingServiceName = service.get('service')?.value;
        return (
          existingServiceName?.toLowerCase() === serviceName?.toLowerCase()
        );
      },
    );

    // Check in the services that are already added in the form (excluding the current service)
    const isDuplicateInForm = this.serviceForm.value.some(
      (service: any, index: number) => {
        // Skip the current service (we don't want to compare it with itself)
        if (index === currentServiceIndex) return false;
        return service.name?.toLowerCase() === serviceName?.toLowerCase();
      },
    );
    return isDuplicateInExisting || isDuplicateInForm;
  }

  onSubmit() {
    if (this.serviceForm.valid) {
      const servicesToAdd = this.serviceForm.value;
      // checking for the duplicate services
      const duplicateService = servicesToAdd.find(
        (service: any, index: number) =>
          this.isDuplicateService(service.name, index),
      );

      if (duplicateService) {
        this.snackBar.error('Duplicate services are not allowed');
        return;
      }

      this.dialogRef.close(this.serviceForm.value);
    }
  }
  closeDialog() {
    this.dialogRef.close();
  }

  convertCurrency(
    row: AbstractControl,
    sourceField: 'fromCurrency' | 'toCurrency',
  ) {
    const roe = this.data.roe || 1;
    if (sourceField === 'fromCurrency') {
      const fromValue = Number(row.get('agentValueFromCurrency')?.value);
      if (!isNaN(fromValue)) {
        const usdValue = fromValue * roe;
        row
          .get('agentValueToCurrency')
          ?.setValue(usdValue.toFixed(2), { emitEvent: false });
      }
    } else if (sourceField === 'toCurrency') {
      const usdValue = Number(row.get('agentValueToCurrency')?.value);
      if (!isNaN(usdValue)) {
        const fromValue = usdValue / roe;
        row
          .get('agentValueFromCurrency')
          ?.setValue(fromValue.toFixed(2), { emitEvent: false });
      }
    }
  }
}
